import React, { useCallback, useEffect, useState } from 'react';
import PeriodoHeader from '../components/PeriodoHeader';
import { usePeriodoComissionamentos } from '../hooks/usePeriodoComissionamentos';
import { EsqueletoTabela, EsqueletoLinhas } from '../../../components/esqueleto/Esqueleto';
import {
  listMovimentacoesRetroativas,
  listBoletosPosFechamento,
  getCompetenciasStatus,
  MovimentacaoRetroativa,
  BoletoPosFechamento,
  CompetenciaStatus,
} from '../../../api/comissionamentosMovimentacoesApi';
import { notify } from '../../../shared/feedback/notify';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-header-row`,
// `recepcao-title`). O import é explícito de propósito: depender de CSS carregado por
// outra tela é acoplamento invisível — foi o que originou o BUG-V74 na at-67.
import '../../recepcao/pages/Recepcao.css';
import './movimentacoes.css';
import DadosAtualizadosEm from '../components/DadosAtualizadosEm';

const brl = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

// Formata DATE/timestamp sem conversão de fuso (campos DATE viriam 1 dia a menos em UTC-3).
const fmtData = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const rowKey = (m: MovimentacaoRetroativa) => `${m.qualificado_tipo}:${m.qualificado_uuid}:${m.periodo}`;

const MovimentacoesRetroativasPage: React.FC = () => {
  const [todos, setTodos] = useState(true);
  const [periodo, setPeriodo] = usePeriodoComissionamentos();
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [totalDelta, setTotalDelta] = useState(0);
  const [rows, setRows] = useState<MovimentacaoRetroativa[]>([]);
  const [statusMap, setStatusMap] = useState<Record<string, CompetenciaStatus>>({});

  // Drill-down por linha
  const [expandido, setExpandido] = useState<string | null>(null);
  const [boletos, setBoletos] = useState<BoletoPosFechamento[]>([]);
  const [boletosLoading, setBoletosLoading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    setExpandido(null);
    try {
      const [resp, status] = await Promise.all([
        listMovimentacoesRetroativas({ periodo: todos ? undefined : periodo, limit: 200 }),
        getCompetenciasStatus(todos ? undefined : periodo),
      ]);
      setRows(resp.data);
      setTotalDelta(resp.totalDelta);
      const map: Record<string, CompetenciaStatus> = {};
      status.data.forEach((s) => {
        map[s.periodo] = s;
      });
      setStatusMap(map);
    } catch (e: any) {
      setErro(e.message || 'Falha ao carregar');
      setRows([]);
      setTotalDelta(0);
    } finally {
      setLoading(false);
    }
  }, [todos, periodo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirDrill = async (m: MovimentacaoRetroativa) => {
    const key = rowKey(m);
    if (expandido === key) {
      setExpandido(null);
      return;
    }
    setExpandido(key);
    setBoletos([]);
    setBoletosLoading(true);
    try {
      const resp = await listBoletosPosFechamento(m.periodo);
      // Atribui pelo id correspondente ao tipo (voluntário é aproximado — ver ADR).
      const campo =
        m.qualificado_tipo === 'REGIONAL'
          ? 'regional_id'
          : m.qualificado_tipo === 'COOPERATIVA'
          ? 'consultor_id'
          : 'equipe_id';
      const filtrados = resp.data.filter((b) => (b as any)[campo] === m.qualificado_uuid);
      setBoletos(filtrados.length ? filtrados : resp.data);
    } catch (error) {
      // Lista vazia sem aviso parece "nenhum boleto no período", o que muda a decisão do acerto.
      setBoletos([]);
      notify.errorFrom(
        error,
        'Não foi possível carregar os boletos desta competência. Recarregue a página; se persistir, acione a TI.'
      );
    } finally {
      setBoletosLoading(false);
    }
  };

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      {/*
        Esqueleto COMPARTILHADO de página. `recepcao-conte-do` e `recepcao-header-row`
        substituem o contêiner `.mov-container`, que tinha margens próprias (padding de
        24px/32px e largura máxima de 1200px centralizada) e não acompanhava as demais
        telas. A classe local abaixo guarda só o respiro inferior.
      */}
      <div className="ConteDo recepcao-conte-do mov-conte-do">
        <div className="recepcao-header-row">
          <div>
            {/* Título no tamanho padrão das outras telas; o subtítulo continua logo abaixo. */}
            <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Movimentações retroativas
            </div>
            <p className="mov-sub">
              Competências cujo repasse <strong>mudou depois de fechado</strong> — normalmente boletos
              pagos que caíram na base após o corte. O valor não é alterado automaticamente; use esta
              lista para decidir o acerto (back-pay).
            </p>
          </div>
        </div>
        <DadosAtualizadosEm />

        <div className="mov-controls">
          <label className="mov-toggle">
            <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} />
            Todos os meses
          </label>
          {!todos && (
            <PeriodoHeader periodo={periodo} onChange={setPeriodo} />
          )}
          <button className="mov-refresh" onClick={carregar} disabled={loading}>
            {loading ? 'Carregando…' : 'Atualizar'}
          </button>
        </div>

        {!loading && !erro && (
          <div className={`mov-banner ${totalDelta === 0 ? 'ok' : totalDelta > 0 ? 'up' : 'down'}`}>
            {rows.length === 0 ? (
              <span>Nenhuma movimentação retroativa — todos os repasses pagos batem com o cálculo atual.</span>
            ) : (
              <span>
                <strong>{rows.length}</strong> competência(s) mudaram após o fechamento · saldo líquido{' '}
                <strong>{brl(totalDelta)}</strong>{' '}
                {totalDelta > 0 ? '(a pagar a mais)' : totalDelta < 0 ? '(pago a mais)' : ''}
              </span>
            )}
          </div>
        )}

        {erro && <div className="mov-erro">{erro}</div>}

        {loading && (
          /* A tabela fica no lugar durante a carga, com as 9 colunas — antes a área
             sumia por inteiro e a página encolhia até o dado chegar. */
          <table className="mov-table" role="status" aria-busy="true">
            <thead>
              <tr>
                <th>Beneficiário</th>
                <th>Tipo</th>
                <th>Competência</th>
                <th className="num">Pago</th>
                <th className="num">Atual</th>
                <th className="num">Δ Delta</th>
                <th>Janela</th>
                <th className="num">Em aberto</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <EsqueletoTabela linhas={5} colunas={9} />
            </tbody>
          </table>
        )}

        {!loading && rows.length > 0 && (
          <table className="mov-table">
            <thead>
              <tr>
                <th>Beneficiário</th>
                <th>Tipo</th>
                <th>Competência</th>
                <th className="num">Pago</th>
                <th className="num">Atual</th>
                <th className="num">Δ Delta</th>
                <th>Janela</th>
                <th className="num">Em aberto</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const key = rowKey(m);
                const aberto = expandido === key;
                return (
                  <React.Fragment key={key}>
                    <tr className={aberto ? 'aberto' : ''}>
                      <td>
                        <span className="mov-nome">{m.nome || '—'}</span>
                        {m.codigo && <span className="mov-codigo">#{m.codigo}</span>}
                      </td>
                      <td>
                        <span className={`mov-tag ${m.qualificado_tipo.toLowerCase()}`}>
                          {m.qualificado_tipo}
                        </span>
                      </td>
                      <td>{m.periodo}</td>
                      <td className="num">{brl(m.valor_pago)}</td>
                      <td className="num">{brl(m.valor_atual)}</td>
                      <td className={`num delta ${m.delta > 0 ? 'up' : m.delta < 0 ? 'down' : ''}`}>
                        {m.delta > 0 ? '+' : ''}
                        {brl(m.delta)}
                      </td>
                      <td className="mov-janela">
                        {(() => {
                          const st = statusMap[m.periodo];
                          if (!st) return <span className="mov-muted">{fmtData(m.fechado_em)}</span>;
                          return (
                            <>
                              <div className="mov-janela-linha">corte {fmtData(st.corte)}</div>
                              {st.congelado ? (
                                <div className="mov-janela-linha ok">fechado {fmtData(st.fechado_em)} ✓</div>
                              ) : (
                                <div className="mov-janela-linha pend">congela {fmtData(st.congela_em)}</div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td className="num" title="Boletos ainda não pagos (situação ABERTO) que venceram nesta competência — a receber. Quando pagos, cairão na competência do pagamento.">
                        {statusMap[m.periodo] ? statusMap[m.periodo].boletos_em_aberto : '—'}
                      </td>
                      <td>
                        <button className="mov-drill" onClick={() => abrirDrill(m)}>
                          {aberto ? 'Fechar' : 'Boletos'}
                        </button>
                      </td>
                    </tr>
                    {aberto && (
                      <tr className="mov-drill-row">
                        <td colSpan={9}>
                          {boletosLoading ? (
                            <div className="mov-drill-loading" role="status" aria-busy="true">
                              <span className="esqueleto-sr-only">Carregando os boletos da competência</span>
                              <EsqueletoLinhas linhas={4} />
                            </div>
                          ) : boletos.length === 0 ? (
                            <div className="mov-drill-empty">
                              Nenhum boleto identificado como caído após o fechamento nesta competência.
                            </div>
                          ) : (
                            <table className="mov-boletos">
                              <thead>
                                <tr>
                                  <th>Vencimento</th>
                                  <th>Pagamento</th>
                                  <th className="num">Valor</th>
                                  <th>Caiu na base</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {boletos.map((b) => (
                                  <tr key={b.entrada_id}>
                                    <td>{fmtData(b.data_vencimento)}</td>
                                    <td>{fmtData(b.data_pagamento)}</td>
                                    <td className="num">{brl(b.valor_pago)}</td>
                                    <td>{fmtData(b.landed_em)}</td>
                                    <td>
                                      {b.pago_em_atraso ? (
                                        <span className="mov-badge atraso">pago em atraso</span>
                                      ) : (
                                        <span className="mov-badge normal">no prazo</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MovimentacoesRetroativasPage;
